import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { TaskService } from '@duck/api-definitions'
import { TaskTableFilters } from './TaskTableFilters'
import { TasksTable } from './TasksTable'
import { TaskDialog } from './TaskDialog'
import { baseColumns, type Column } from './utils'
import { useTar } from '@/modules'
import { useAdminTasks } from '@/hooks/useAdminTasks'

const STORAGE_KEY = 'admin-task-filters'

interface FilterState {
  sortField: keyof TaskService.Task
  sortDirection: 'asc' | 'desc'
  filterText: string
  difficultyFilter: string[]
  visibleColumnKeys: string[]
}

// Utility functions for localStorage
const loadFiltersFromStorage = (): Partial<FilterState> | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const saveFiltersToStorage = (filters: FilterState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // localStorage might be disabled, silently fail
  }
}

// Helper to reconstruct columns from base + visible keys
const reconstructColumns = (visibleKeys?: string[]): Column[] => {
  if (!visibleKeys) {
    return baseColumns
  }
  return baseColumns.map(col => ({
    ...col,
    visible: visibleKeys.includes(col.key)
  }))
}

export const TasksManagement: React.FC = () => {
  const { t } = useTar()
  // Initialize state from localStorage or defaults
  const storedFilters = loadFiltersFromStorage()

  const [sortField, setSortField] = useState<keyof TaskService.Task>(
    storedFilters?.sortField ?? 'name'
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    storedFilters?.sortDirection ?? 'asc'
  )
  const [filterText, setFilterText] = useState(storedFilters?.filterText ?? '')
  const [difficultyFilter, setDifficultyFilter] = useState<string[]>(
    Array.isArray(storedFilters?.difficultyFilter) ? storedFilters?.difficultyFilter as string[] : (storedFilters?.difficultyFilter ? [storedFilters?.difficultyFilter as unknown as string] : [])
  )
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
  const [columns, setColumns] = useState<Column[]>(
    () => reconstructColumns(storedFilters?.visibleColumnKeys)
  )

  // Dialog state
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState<TaskService.Task | null>(null)

  // Save to localStorage whenever filter state changes
  useEffect(() => {
    const visibleColumnKeys = columns.filter(col => col.visible).map(col => col.key)
    const filterState: FilterState = {
      sortField,
      sortDirection,
      filterText,
      difficultyFilter,
      visibleColumnKeys,
    }
    saveFiltersToStorage(filterState)
  }, [sortField, sortDirection, filterText, difficultyFilter, columns])

  const {
    tasks,
    hasMore,
    isInitialLoading,
    isFetchingNextPage,
    isError,
    fetchNextPage,
    refetch,
  } = useAdminTasks({
    sortField,
    sortDirection,
  })

  // Derive allAccessGroups from tasks
  const allAccessGroups = useMemo(() => {
    return tasks.flatMap(task => task.accessGroups)
  }, [tasks])

  const filteredTasks = useMemo(() => {
    let filtered = tasks

    // Difficulty filter (client-side for now)
    if (difficultyFilter.length > 0) {
      filtered = filtered.filter(task => difficultyFilter.includes(task.difficulty as string))
    }

    // Text filter (client-side for now)
    if (filterText) {
      const searchTerm = filterText.toLowerCase()
      filtered = filtered.filter(task =>
        (task.name || '').toLowerCase().includes(searchTerm) ||
        (task.description || '').toLowerCase().includes(searchTerm)
      )
    }

    return filtered
  }, [tasks, difficultyFilter, filterText])

  const handleLoadMorePage = useCallback(() => {
    if (!hasMore) return
    void fetchNextPage()
  }, [hasMore, fetchNextPage])

  const handleSort = (field: keyof TaskService.Task) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const toggleColumnVisibility = (columnKey: string) => {
    setColumns(prev => prev.map(col =>
      col.key === columnKey ? { ...col, visible: !col.visible } : col
    ))
  }

  const handleTaskClick = (task: TaskService.Task) => {
    setSelectedTask(task)
    setIsTaskDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsTaskDialogOpen(false)
    setSelectedTask(null)
  }

  const handleCreateNewTask = () => {
    setSelectedTask(null)
    setIsTaskDialogOpen(true)
  }

  const visibleColumns = columns.filter(col => col.visible)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
            {t('tasks.taskManagement')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('tasks.taskManagementDescription')}
          </p>
        </div>
        <button
          onClick={handleCreateNewTask}
          className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('tasks.addTask')}
        </button>
      </div>

      {/* Filters and Controls */}
      <TaskTableFilters
        filterText={filterText}
        setFilterText={setFilterText}
        difficultyFilter={difficultyFilter}
        setDifficultyFilter={setDifficultyFilter}
        columns={columns}
        columnDropdownOpen={columnDropdownOpen}
        setColumnDropdownOpen={setColumnDropdownOpen}
        toggleColumnVisibility={toggleColumnVisibility}
      />

      {/* Error State */}
      {isError && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">
            {t('common.error')}
          </div>
        </div>
      )}

      {/* Tasks Table */}
      {!isError && (
        <TasksTable
          tasks={filteredTasks}
          totalTasks={tasks.length}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onTaskClick={handleTaskClick}
          loadingMode={isInitialLoading}
          hasMore={hasMore}
          onLoadMore={handleLoadMorePage}
          isPageLoading={isFetchingNextPage}
        />
      )}

      {/* Task Dialog */}
      <TaskDialog
        isOpen={isTaskDialogOpen}
        onClose={handleCloseDialog}
        task={selectedTask}
        onTaskSaved={() => { void refetch() }}
        allAccessGroups={allAccessGroups}
      />
    </div>
  )
}
